import { defineStore } from 'pinia';
import { computed, ref } from 'vue';

export type Role = 'administrator' | 'teacher' | 'student' | 'applicant';

export interface UserOut {
  id: number;
  email: string;
  first_name: string;
  last_name: string;
  role: Role;
  is_active: boolean;
}

export interface LoginOut {
  access_token: string;
  refresh_token: string;
  token_type: 'bearer';
  expires_in: number;
  user: UserOut;
}

const STORAGE_KEY = 'externist-portal.auth.v1';

interface PersistedAuth {
  accessToken: string;
  refreshToken: string;
  expiresAt: number;
  user: UserOut;
}

function decodeJwtExp(token: string): number | null {
  try {
    const parts = token.split('.');
    if (parts.length !== 3) return null;
    const payloadPart = parts[1];
    if (!payloadPart) return null;
    const payload = JSON.parse(atob(payloadPart.replace(/-/g, '+').replace(/_/g, '/'))) as {
      exp?: number;
    };
    return typeof payload.exp === 'number' ? payload.exp * 1000 : null;
  } catch {
    return null;
  }
}

function loadFromStorage(): PersistedAuth | null {
  if (typeof window === 'undefined') return null;
  try {
    const raw = window.localStorage.getItem(STORAGE_KEY);
    if (!raw) return null;
    const parsed = JSON.parse(raw) as Partial<PersistedAuth>;
    if (
      typeof parsed.accessToken !== 'string' ||
      typeof parsed.refreshToken !== 'string' ||
      typeof parsed.expiresAt !== 'number' ||
      !parsed.user ||
      typeof parsed.user.id !== 'number' ||
      typeof parsed.user.email !== 'string' ||
      typeof parsed.user.role !== 'string'
    ) {
      return null;
    }
    return {
      accessToken: parsed.accessToken,
      refreshToken: parsed.refreshToken,
      expiresAt: parsed.expiresAt,
      user: parsed.user as UserOut,
    };
  } catch {
    return null;
  }
}

function saveToStorage(data: PersistedAuth | null): void {
  if (typeof window === 'undefined') return;
  try {
    if (data === null) {
      window.localStorage.removeItem(STORAGE_KEY);
    } else {
      window.localStorage.setItem(STORAGE_KEY, JSON.stringify(data));
    }
  } catch {
    // swallow quota / privacy errors
  }
}

export const useAuthStore = defineStore('auth', () => {
  const initial = loadFromStorage();

  const accessToken = ref<string | null>(initial?.accessToken ?? null);
  const refreshToken = ref<string | null>(initial?.refreshToken ?? null);
  const expiresAt = ref<number | null>(initial?.expiresAt ?? null);
  const user = ref<UserOut | null>(initial?.user ?? null);

  const isAuthenticated = computed(() => accessToken.value !== null && user.value !== null);
  const role = computed<Role | null>(() => user.value?.role ?? null);

  function setAuth(login: LoginOut): void {
    accessToken.value = login.access_token;
    refreshToken.value = login.refresh_token;
    const tokenExp = decodeJwtExp(login.access_token);
    expiresAt.value = tokenExp ?? Date.now() + login.expires_in * 1000;
    user.value = login.user;
    saveToStorage({
      accessToken: login.access_token,
      refreshToken: login.refresh_token,
      expiresAt: expiresAt.value,
      user: login.user,
    });
  }

  function setUser(nextUser: UserOut): void {
    user.value = nextUser;
    if (accessToken.value && refreshToken.value && expiresAt.value !== null) {
      saveToStorage({
        accessToken: accessToken.value,
        refreshToken: refreshToken.value,
        expiresAt: expiresAt.value,
        user: nextUser,
      });
    }
  }

  function setAccessToken(token: string, expiresIn?: number): void {
    accessToken.value = token;
    const tokenExp = decodeJwtExp(token);
    expiresAt.value = tokenExp ?? (expiresIn ? Date.now() + expiresIn * 1000 : null);
    if (refreshToken.value && user.value && expiresAt.value !== null) {
      saveToStorage({
        accessToken: token,
        refreshToken: refreshToken.value,
        expiresAt: expiresAt.value,
        user: user.value,
      });
    }
  }

  function clearAuth(): void {
    accessToken.value = null;
    refreshToken.value = null;
    expiresAt.value = null;
    user.value = null;
    saveToStorage(null);
  }

  function getAccessToken(): string | null {
    return accessToken.value;
  }

  function getRefreshToken(): string | null {
    return refreshToken.value;
  }

  function isExpired(): boolean {
    if (expiresAt.value === null) return accessToken.value === null;
    // treat tokens expiring in the next 5 seconds as expired (clock skew buffer)
    return Date.now() >= expiresAt.value - 5000;
  }

  return {
    accessToken,
    refreshToken,
    expiresAt,
    user,
    role,
    isAuthenticated,
    setAuth,
    setUser,
    setAccessToken,
    clearAuth,
    getAccessToken,
    getRefreshToken,
    isExpired,
  };
});
